import React from "react"

// Constants
import {
    DEFAULT_UI_SKIN,
    DEFAULT_UI_THEME,
    UI_SKIN_QUERY_PARAM,
    UI_SKIN_STORAGE_KEY,
    UI_STORAGE_VERSION,
    UI_STORAGE_VERSION_KEY,
    UI_THEME_QUERY_PARAM,
    UI_THEME_STORAGE_KEY,
    UiSkin,
    UiTheme,
} from "../constants"

export type UiSkinContextValue = {
    skin: UiSkin
    isModern: boolean
    isClassic: boolean
    setSkin: (skin: UiSkin) => void
    toggleSkin: () => void
    /** Always "light" while `skin` is "classic" — Classic is light-only. */
    theme: UiTheme
    isDark: boolean
    toggleTheme: () => void
}

const UiSkinContext = React.createContext<UiSkinContextValue | null>(null);

/**
 * Reads a query param without URLSearchParams, which CRA 3.3 does not polyfill
 * and older embedded Safari builds in our target matrix lack.
 */
function readQueryParam(name: string): string | null {
    try {
        const match = new RegExp("[?&]" + name + "=([^&#]*)").exec(window.location.search);

        return match ? decodeURIComponent(match[1]) : null
    } catch (e) {
        return null
    }
}

function readStorage(key: string): string | null {
    try {
        return window.localStorage.getItem(key)
    } catch (e) {
        // embedded webviews and Safari private mode throw on access
        return null
    }
}

function writeStorage(key: string, value: string) {
    try {
        window.localStorage.setItem(key, value)
    } catch (e) {
        // non-fatal: the skin simply will not persist across reloads
    }
}

function removeStorage(key: string) {
    try {
        window.localStorage.removeItem(key)
    } catch (e) {
    }
}

function migrateStorage() {
    if (readStorage(UI_STORAGE_VERSION_KEY) === UI_STORAGE_VERSION) {
        return
    }

    removeStorage(UI_SKIN_STORAGE_KEY);
    writeStorage(UI_STORAGE_VERSION_KEY, UI_STORAGE_VERSION)
}

function isSkin(value: string | null): value is UiSkin {
    return value === "classic" || value === "modern"
}

function isTheme(value: string | null): value is UiTheme {
    return value === "light" || value === "dark"
}

/**
 * `?ui=` / `?theme=` are BOOT-TIME ONE-SHOT overrides that write through to
 * localStorage. They deliberately do not survive navigation: every
 * `history.push` in this app drops `location.search`, PrivateRoute's redirect
 * carries only pathname + state, and the catch-all route is a bare
 * `<Redirect to={AppRoute.Home}/>`. After first load, localStorage is the
 * source of truth. Do not "fix" this by threading search params through the
 * router — nothing depends on the param surviving.
 */
/**
 * `isExplicit` marks a value the user actually asked for — a `?ui=` param or a
 * previously persisted choice — as opposed to one that fell through to the
 * DEFAULT. Only an explicit value may be written back to storage. Persisting a
 * resolved default would pin whatever DEFAULT_UI_SKIN happened to be on a
 * browser's first ever load, making the constant permanently unchangeable for
 * everyone who has already opened the app.
 */
type ResolvedSkin = {value: UiSkin, isExplicit: boolean}
type ResolvedTheme = {value: UiTheme, isExplicit: boolean}

function resolveInitialSkin(): ResolvedSkin {
    migrateStorage();

    const fromQuery = readQueryParam(UI_SKIN_QUERY_PARAM);
    if (isSkin(fromQuery)) {
        return {value: fromQuery, isExplicit: true}
    }

    const fromStorage = readStorage(UI_SKIN_STORAGE_KEY);
    if (isSkin(fromStorage)) {
        return {value: fromStorage, isExplicit: true}
    }

    return {value: DEFAULT_UI_SKIN, isExplicit: false}
}

function resolveInitialTheme(): ResolvedTheme {
    const fromQuery = readQueryParam(UI_THEME_QUERY_PARAM);
    if (isTheme(fromQuery)) {
        return {value: fromQuery, isExplicit: true}
    }

    const fromStorage = readStorage(UI_THEME_STORAGE_KEY);
    if (isTheme(fromStorage)) {
        return {value: fromStorage, isExplicit: true}
    }

    return {value: DEFAULT_UI_THEME, isExplicit: false}
}

/**
 * @param props
 */
export function UiSkinProvider(props: {children: React.ReactNode}) {
    // Lazy initialisers run synchronously before first paint, so there is no
    // classic-flash-then-modern on reload. This also means the provider needs
    // no router context and can sit above <BrowserRouter/>.
    const [initialSkin] = React.useState<ResolvedSkin>(resolveInitialSkin);
    const [initialTheme] = React.useState<ResolvedTheme>(resolveInitialTheme);
    const [skin, setSkinState] = React.useState<UiSkin>(initialSkin.value);
    const [storedTheme, setStoredTheme] = React.useState<UiTheme>(initialTheme.value);

    // Classic is light-only: it must never observe the reading-room palette,
    // even if the user left dark enabled before switching back.
    const theme: UiTheme = skin === "modern" ? storedTheme : "light";

    React.useEffect(() => {
        document.documentElement.dataset.ui = skin;
    }, [skin]);

    React.useEffect(() => {
        document.documentElement.dataset.theme = theme;
    }, [theme]);

    // Boot-time `?ui=` / `?theme=` write-through, as documented above. A value
    // that fell through to the DEFAULT is deliberately NOT written: storage
    // records choices, never defaults.
    React.useEffect(() => {
        if (initialSkin.isExplicit) {
            writeStorage(UI_SKIN_STORAGE_KEY, initialSkin.value)
        }

        if (initialTheme.isExplicit) {
            writeStorage(UI_THEME_STORAGE_KEY, initialTheme.value)
        }
    }, [initialSkin, initialTheme]);

    const setSkin = React.useCallback((next: UiSkin) => {
        const value: UiSkin = next === "classic" ? "classic" : "modern";

        writeStorage(UI_SKIN_STORAGE_KEY, value);
        setSkinState(value);
    }, []);

    const toggleSkin = React.useCallback(() => {
        setSkin(skin === "classic" ? "modern" : "classic");
    }, [skin, setSkin]);

    const toggleTheme = React.useCallback(() => {
        const next: UiTheme = storedTheme === "dark" ? "light" : "dark";

        writeStorage(UI_THEME_STORAGE_KEY, next);
        setStoredTheme(next);
    }, [storedTheme]);

    const value = React.useMemo<UiSkinContextValue>(() => ({
        skin,
        isModern: skin === "modern",
        isClassic: skin === "classic",
        setSkin,
        toggleSkin,
        theme,
        isDark: theme === "dark",
        toggleTheme,
    }), [skin, theme, setSkin, toggleSkin, toggleTheme]);

    return (
        <UiSkinContext.Provider value={value}>
            {props.children}
        </UiSkinContext.Provider>
    )
}

export function useUiSkin(): UiSkinContextValue {
    const ctx = React.useContext(UiSkinContext);

    if (ctx === null) {
        throw new Error("useUiSkin must be used within a UiSkinProvider")
    }

    return ctx
}

export default UiSkinContext
